import { z } from 'zod';

/**
 * Identity-bearing claims of a BrightLocal access token. The ids are strings, and the
 * `impersonator_user_id` claim is omitted (not null) outside a Login As session — the
 * token generator drops null claims. Shape mirrors `JwtClaims` in the Tools backend.
 */
const IdentityClaimsSchema = z
  .object({
    user_id: z.string(),
    customer_id: z.string(),
    impersonator_user_id: z.string().nullish()
  })
  .transform(({ user_id: userId, customer_id: customerId, impersonator_user_id: impersonatorUserId }) => ({
    userId,
    customerId,
    impersonatorUserId: impersonatorUserId ?? null
  }));

/** The identity a BrightLocal access token was issued for. */
export type IdentityClaims = z.infer<typeof IdentityClaimsSchema>;

const decodeBase64Url = (value: string) => atob(value.replace(/-/g, '+').replace(/_/g, '/'));

/**
 * Extracts the identity claims from a JWT access token without verifying the
 * signature — the token comes from our own auth endpoint, verification is the
 * backend's job. Returns null when the token cannot be decoded or the identity
 * claims are missing.
 */
export const extractIdentityClaims = (accessToken: string): IdentityClaims | null => {
  const payload = accessToken.split('.')[1];

  if (!payload) return null;

  try {
    const parsed = IdentityClaimsSchema.safeParse(JSON.parse(decodeBase64Url(payload)));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

/**
 * True when two access tokens were issued for different identities — a Login As /
 * Switch back happened on the old platform between the two refreshes. Undecodable
 * tokens compare as "no change" so a malformed token can't trigger a reload loop.
 */
export const hasIdentityChanged = (previousToken: string | null, nextToken: string): boolean => {
  if (previousToken === null) return false;

  const previous = extractIdentityClaims(previousToken);
  const next = extractIdentityClaims(nextToken);

  if (previous === null || next === null) return false;

  return (
    previous.userId !== next.userId ||
    previous.customerId !== next.customerId ||
    previous.impersonatorUserId !== next.impersonatorUserId
  );
};
